import React, { useState, useEffect } from 'react';
import {
  Col, Row, Button, Input, Card, notification,
  Modal, Tooltip, Form,
} from 'antd';
import {
  CheckCircleOutlined,
  CheckCircleTwoTone,
  InfoCircleTwoTone,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  get, put, RequestWrapper, ResponseWrapper, isSuperAgentError,
} from '../../dataSource/requests';
import { removeUserFromStorage } from '../../utils/users';

import MatriculationHandle from './MatriculationHandle';
import { GENERIC_ERROR_MESSAGE } from '../../utils/responseMessages';
import { namespaces } from '../../i18n/i18n.constants';
import { formatTelephone } from '../../utils/format';

import './ProfileEdit.scss';

const ProfileEdit = (): JSX.Element => {
  const { t } = useTranslation('namespaces');
  const [formInstance] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const [matriculationArray, setMatriculationArray] = useState([
    {
      id: 1,
      value: '',
      error: false,
      status: '',
      errorMessage: '',
      equalsId: [],
      back: true,
    },
  ]);

  const [initialValues, setInitialValues] = useState({
    email: '',
    telephone: '',
    name: '',
  });

  const [matriculations, setMatriculations] = useState([
    {
      matriculation: '',
      // ACTIVATED, DEACTIVATED e PENDING_APPROVAL
      status: 'PENDING_APPROVAL',
    },
  ]);

  const [initalStatus, setInitalStatus] = useState({});

  const handleMATRICULA = (newMatriculationArray): void => {
    setMatriculationArray(newMatriculationArray);
  };

  const validatePhone = (rule, value): Promise<void> => {
    if (value === '') {
      return Promise.reject(new Error('Por favor, insira um telefone.'));
    }

    const numbers = value.replace(/\D/g, ''); // Only numbers

    if (numbers.length !== 10 && numbers.length !== 11) {
      return Promise.reject(new Error('Por favor, insira um telefone válido.'));
    }

    return Promise.resolve();
  };

  const checkMatriculationsErrors = (): boolean => {
    const stateCopy = matriculationArray;

    for (let index = 0; index < stateCopy.length; index += 1) {
      if (stateCopy[index].error) {
        stateCopy[index].error = true;

        setMatriculationArray(stateCopy);

        return false;
      }

      if (matriculationArray[index].value.length < 10) {
        stateCopy[index].error = true;
        stateCopy[index].errorMessage = 'Matrícula menor que 10 dígitos';

        setMatriculationArray(stateCopy);

        return false;
      }
    }

    return true;
  };

  const checkEmailValidation = (): JSX.Element => {
    if (!initialValues.emailConfirmed) {
      return (
        <Tooltip title="Email não confirmado">
          <InfoCircleTwoTone twoToneColor="#eb2f96" />
        </Tooltip>
      );
    } return (
      <Tooltip title="Email confirmado">
        <CheckCircleOutlined style={{ color: 'green' }} />
      </Tooltip>
    );
  };

  const handleOkAtCancelModal = (): void => {
    navigate('/home');
  };

  const handleCancel = (): void => {
    let updated = false;
    if (matriculationArray.length === initialValues.matriculations.length) {
      for (let index = 0; index < initialValues.matriculations.length; index += 1) {
        if (initialValues.matriculations[index].matriculation !== matriculationArray[index].value) {
          updated = true;
        }
      }
    }

    if (initialValues.matriculations.length !== matriculationArray.length) {
      updated = true;
    }

    if (initialValues.name !== formInstance.getFieldsValue().name) {
      updated = true;
    }

    if (initialValues.email.toLowerCase() !== formInstance.getFieldsValue().email.toLowerCase()) {
      updated = true;
    }

    if (initialValues.telephone !== formInstance.getFieldsValue().telephone) {
      updated = true;
    }

    if (updated === true) {
      Modal.confirm({
        title: 'Os dados alterados serão perdidos!',
        content: 'Tem certeza que deseja cancelar?',
        okText: 'Sim, cancelar sem salvar',
        cancelText: 'Não',
        autoFocusButton: null,
        icon: (<WarningOutlined />),
        onOk: handleOkAtCancelModal,
      });
    } else {
      navigate('/home');
    }
  };

  const handleSubmit = async (values): Promise<void> => {
    setLoading(true);

    if (checkMatriculationsErrors()) {
      const params = {
        email: values.email.toLowerCase(),
        name: values.name,
        telephone: values.telephone.replace(/\D/g, ''),
      };

      const matriculationsAux = matriculationArray.map((v) => v.value);
      const initialMatriculationsAux = matriculations.map((v) => v.matriculation);

      let matBool = false;

      if (matriculationsAux.length !== initialMatriculationsAux.length) {
        matBool = true;
      } else {
        for (let index = 0; index < initialMatriculationsAux.length; index += 1) {
          if (!initialMatriculationsAux.includes(matriculationsAux[index])) {
            matBool = true;
          }
        }
      }

      params.matriculations = matriculationsAux;

      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = put({
        url: '/customer/myProfile/edit',
        params,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 1015):
          // "Customer profile edited successfully with email change", 1015
          removeUserFromStorage();

          Modal.confirm({
            cancelButtonProps: { style: { display: 'none' } },
            title: 'Dados atualizados com sucesso!',
            content: 'Agora é necessário confirmar o e-mail alterado e fazer login novamente. Verifique sua caixa de entrada.',
            cancelType: 'cancel',
            autoFocusButton: null,
            icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
          });

          navigate('/login');
          break;

        case (response?.body?.code === 1016):
          // "Customer profile edited successfully without email change", 1016
          notification.success({
            message: !matBool
              ? 'Dados atualizados com sucesso!'
              : 'Dados atualizados com sucesso! O acesso às informações das matrículas adicionadas depende da aprovação dessas matrículas pela nossa equipe.',

            description: '',
          });

          navigate('/home');
          break;

        case (responseError?.body?.code === -1000):
          // "Email already in use", -1000
          notification.error({
            message: 'Erro',
            description: 'USER_REGISTRATION_MESSAGES.EMAIL_ALREADY_IN_USE',
          });
          break;

        case (responseError?.body?.code === -1007):
          // "User not found", -1007
          notification.error({
            message: 'Erro',
            description: 'Usuário não encontrado.',
          });
          break;

        case (responseError?.body?.code === -2000):
          // "Matriculation cannot be null", -2000
          notification.error({
            message: 'Erro',
            description: 'Matrícula não pode ser nula.',
          });
          break;

        case (responseError?.body?.code === -2005):
          // There are no valid matriculations, -2005
          notification.error({
            message: 'Erro',
            description: 'Não há matrículas ativas.',
          });
          break;

        case (responseError?.body?.code === -19000):
          // "SAPTimeOut", -19000
          notification.error({
            message: t('error', { ns: namespaces.common }),
            description: t('SAPTimeOut', { ns: namespaces.error.sap }),
          });
          break;

        case (responseError?.body?.code === -19001):
          // "SAPNotFound", -19001
          notification.error({
            message: t('error', { ns: namespaces.common }),
            description: t('SAPNotFound', { ns: namespaces.error.sap }),
          });
          break;

        case (responseError?.body?.code === -19002):
          // "SAPBadRequest", -19002
          notification.error({
            message: t('error', { ns: namespaces.common }),
            description: t('SAPBadRequest', { ns: namespaces.error.sap }),
          });
          break;

        case (responseError?.body?.code === -2001):
          // "Duplicated matriculations", -2001
          notification.error({
            message: 'Erro',
            description: 'Matrículas duplicadas.',
          });
          break;

        case (responseError?.body?.code === -2004):
          // "Customer must have at least one matriculation", -2004
          notification.error({
            message: 'Erro',
            description: 'Ao menos uma matrícula deve ser informada.',
          });
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: 'Você não possui permissão para realizar esta ação.',
          });
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: GENERIC_ERROR_MESSAGE,
          });
          break;
      }

      setLoading(false);
    }
  };

  useEffect(() => {
    const firstMount = async (): Promise<void> => {
      // if is editing, fetch user data

      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError;

      const requestReference: RequestWrapper = get({
        url: '/customer/myProfile/find',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 1014): {
          // "Customer profile found successfully", 1014
          const data = response.body.customer;
          const newMatriculationArray = [];
          const newInitalStatus = {};

          data.matriculations.forEach((item, index) => {
            newMatriculationArray.push({
              id: index + 1,
              value: item.matriculation,
              error: false,
              status: 'success',
              errorMessage: '',
              back: true,
              equalsId: [],
            });

            newInitalStatus[index + 1] = item.status;
          });

          const mask = (rawValue) => {
            const value = rawValue.replace(/\D/g, ''); // Only numbers

            if (value.length <= 10) {
              return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6, 10)}`;
            }

            return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
          };

          setInitialValues((previous) => {
            return ({
              ...previous,
              ...data,
              telephone: mask(data.telephone),
            });
          });

          formInstance.setFieldsValue({
            ...data,
            telephone: mask(data.telephone),
          });

          setMatriculations(data.matriculations);
          setMatriculationArray(newMatriculationArray);
          setInitalStatus(newInitalStatus);
        } break;

        case (responseError?.body?.code === -1007):
          // "User not found", -1007
          notification.error({
            message: 'Erro',
            description: 'Usuário não encontrado.',
          });
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: 'Você não possui permissão para realizar esta ação.',
          });
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: GENERIC_ERROR_MESSAGE,
          });
          break;
      }
    };

    firstMount();

    return () => {
      setLoading(false);
    };
  }, []);

  return (
    <div className="ProfileEdit">
      <Row gutter={24} className="title-container">
        <Col xs={{ span: 24 }} md={{ span: 16 }}>
          <span className="page-title">Meu perfil</span>
        </Col>
        <Col xs={{ span: 24 }} md={{ span: 8 }}>
          <Button
            className="cancel"
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          {/* <Button
            className="save"
            type="primary"
            onClick={() => {
              formInstance.submit();
            }}
            loading={loading}
          >
            {loading ? 'Salvando' : 'Salvar'}
          </Button> */}
        </Col>
      </Row>

      <Card className="inputs-wrapper">
        <Row>
          <Col
            sm={{ span: 24 }}
            md={{ span: 12 }}
            lg={{ span: 12 }}
          >
            <Form
              scrollToFirstError
              className="inputs"
              layout="vertical"
              form={formInstance}
              onFinish={handleSubmit}
              onFinishFailed={
                (errorInfo): void => {
                  console.log('profileedit onFinishFailed', errorInfo);
                }
              }
            >
              <Form.Item
                label="Nome"
                name="name"
                initialValue={initialValues.name}
                rules={[
                  {
                    message: 'O campo não pode ser vazio',
                  },
                  {
                    required: true, message: 'Por favor, insira um nome.',
                  },
                  {
                    max: 150, message: 'O nome deve conter no máximo 150 caracteres!',
                  },
                  {
                    min: 3, message: 'O nome deve conter no mínimo 3 caracteres!',
                  },
                ]}
                getValueFromEvent={
                  (eventt) => eventt.target.value.replace(/[^A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ' ]+$/, '')
                }
              >
                <Input
                  name="titulo"
                  placeholder="Nome"
                  disabled
                />
              </Form.Item>

              <Form.Item
                label="E-mail"
                name="email"
                initialValue={initialValues.email}
                rules={[
                  { required: true, message: 'Por favor, insira um email.' },
                  {
                    type: 'email',
                    message: 'Formato de email inválido.',
                  },
                  {
                    pattern: /^[a-z,A-z,0-9,@,.,_,-]+$/,
                    message: 'Não utilize caracteres especiais, ou acentuados.',
                  },
                ]}
              >
                <Input
                  autoComplete="new-email"
                  type="email"
                  placeholder="E-mail"
                  suffix={checkEmailValidation()}
                  disabled
                />
              </Form.Item>

              <Form.Item
                label="Telefone"
                name="telephone"
                initialValue={initialValues.telephone}
                rules={[
                  {
                    trigger: ['onBlur'],
                    validator: validatePhone,
                    required: true,
                  },
                ]}
                getValueFromEvent={
                  (eventt) => formatTelephone(eventt.target.value)
                }
              >
                <Input
                  type="text"
                  placeholder="(00) 00000-0000"
                  className="ant-input"
                  disabled
                />
              </Form.Item>
            </Form>
          </Col>
          <Col
            sm={{ span: 24 }}
            md={{ span: 12 }}
            lg={{ span: 12 }}
          >
            <div className="mat-title">
              Matrículas:
            </div>
            <MatriculationHandle
              form={formInstance}
              handleMATRICULA={handleMATRICULA}
              matriculationArray={matriculationArray}
              initialMatriculations={matriculations}
              initalStatus={initalStatus}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

ProfileEdit.propTypes = {};
ProfileEdit.defaultProps = {};

export default ProfileEdit;
